import { z } from 'zod';
import { scanningMediaSemanticSnapshotSchema, scanningMediaSha256Schema, scanningMediaSourceUnitsSchema, scanningMediaStableKeySchema, } from './scanning-media-semantic-geometry.contract.js';
export const scanningMediaCoordinateContinuityCapability = 'scanning-media.coordinate-continuity.v1';
const coordinateMillimeterSchema = z.number().int().min(-1_000_000).max(1_000_000);
const rotationMilliDegreeSchema = z.number().int().min(-360_000).max(360_000);
export const scanningMediaCoordinateTransformSchema = z.strictObject({
    translation: z.strictObject({
        xMm: coordinateMillimeterSchema,
        yMm: coordinateMillimeterSchema,
        zMm: coordinateMillimeterSchema,
    }),
    yawMilliDegrees: rotationMilliDegreeSchema,
});
export const scanningMediaCoordinateTrustSchema = z.enum([
    'trusted',
    'partial',
    'mixed',
    'ambiguous',
    'untrusted',
]);
export const scanningMediaCoordinateTrustAssessmentSchema = z.strictObject({
    status: scanningMediaCoordinateTrustSchema,
    reasons: z.array(z.string().trim().min(1).max(240)).min(1).max(20),
});
export const scanningMediaCoordinateFloorMappingSchema = z.strictObject({
    sourceFloorKey: scanningMediaStableKeySchema,
    visitFloorKey: scanningMediaStableKeySchema,
    visitFloorLabel: z.string().trim().min(1).max(180),
});
export const scanningMediaCoordinateRoomMappingSchema = z.strictObject({
    sourceRoomKey: scanningMediaStableKeySchema,
    visitRoomKey: scanningMediaStableKeySchema,
    visitRoomLabel: z.string().trim().min(1).max(180),
});
export const scanningMediaCoordinateSourceDeclarationSchema = z.strictObject({
    captureId: z.string().uuid(),
    semanticVersionId: z.string().uuid(),
    transformToVisitFrame: scanningMediaCoordinateTransformSchema,
    trust: scanningMediaCoordinateTrustAssessmentSchema,
    floorMappings: z.array(scanningMediaCoordinateFloorMappingSchema).min(1).max(25),
    roomMappings: z.array(scanningMediaCoordinateRoomMappingSchema).min(1).max(250),
    artifactVersionIds: z.array(z.string().uuid()).max(100).default([]),
});
export const scanningMediaCoordinateRelationshipSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    sourceSemanticVersionId: z.string().uuid(),
    targetSemanticVersionId: z.string().uuid(),
    type: z.enum(['overlap', 'doorway', 'stairs', 'hallway', 'control-points', 'manual']),
    evidence: z.enum(['semantic-geometry', 'asset-anchors', 'operator-review', 'import']),
    confidenceBasisPoints: z.number().int().min(0).max(10_000),
    residualMm: z.number().int().min(0).max(100_000).nullable(),
    trust: scanningMediaCoordinateTrustSchema,
    notes: z.string().trim().min(1).max(500),
});
export const scanningMediaCoordinateFloorEvidenceSchema = z.strictObject({
    sourceFloorKey: scanningMediaStableKeySchema,
    sourceElevationMm: coordinateMillimeterSchema,
    visitFloorKey: scanningMediaStableKeySchema,
    visitFloorLabel: z.string().trim().min(1).max(180),
    visitElevationMm: coordinateMillimeterSchema,
});
export const scanningMediaCoordinateArtifactEvidenceSchema = z.strictObject({
    assetId: z.string().uuid(),
    assetVersionId: z.string().uuid(),
    kind: z.enum([
        'raw-roomplan', 'usdz-model', 'detail-mesh', 'posed-photo', 'panorama', 'depth-map',
        'floor-plan-preview', 'measurement-report', 'scan-package', 'thumbnail', 'other',
    ]),
    sha256: scanningMediaSha256Schema,
});
export const scanningMediaCoordinateSourceEvidenceSchema = z.strictObject({
    ordinal: z.number().int().nonnegative(),
    captureId: z.string().uuid(),
    semanticVersionId: z.string().uuid(),
    componentId: scanningMediaStableKeySchema,
    semanticContentHash: scanningMediaSha256Schema,
    sourceUnits: scanningMediaSourceUnitsSchema,
    capturedAt: z.string().datetime({ offset: true }),
    sourceAccepted: z.boolean(),
    transformToVisitFrame: scanningMediaCoordinateTransformSchema,
    trust: scanningMediaCoordinateTrustAssessmentSchema,
    floorEvidence: z.array(scanningMediaCoordinateFloorEvidenceSchema).min(1).max(25),
    roomMappings: z.array(scanningMediaCoordinateRoomMappingSchema).min(1).max(250),
    artifacts: z.array(scanningMediaCoordinateArtifactEvidenceSchema).max(100),
});
export const scanningMediaCoordinateValidationIssueSchema = z.strictObject({
    severity: z.enum(['warning', 'error']),
    code: z.enum([
        'SOURCE_NOT_ACCEPTED',
        'SOURCE_AUTHORITY_UNSUPPORTED',
        'SOURCE_TRUST_INCOMPLETE',
        'SOURCE_UNITS_MIXED',
        'SOURCE_COMPONENT_DUPLICATE',
        'RELATIONSHIP_AMBIGUOUS',
        'RELATIONSHIP_GRAPH_DISCONNECTED',
        'ARTIFACT_PROVENANCE_INCOMPLETE',
    ]),
    sourceSemanticVersionIds: z.array(z.string().uuid()).max(50),
    message: z.string().trim().min(1).max(500),
});
export const scanningMediaCoordinateValidationSchema = z.strictObject({
    ok: z.boolean(),
    sourceCount: z.number().int().min(2).max(50),
    relationshipCount: z.number().int().min(1).max(1_000),
    connectedComponentCount: z.number().int().positive().max(50),
    sourceUnits: z.array(scanningMediaSourceUnitsSchema).min(1).max(2),
    issues: z.array(scanningMediaCoordinateValidationIssueSchema).max(500),
    errorCount: z.number().int().nonnegative(),
    warningCount: z.number().int().nonnegative(),
});
export const scanningMediaCoordinateDecisionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    candidateId: z.string().uuid(),
    decision: z.enum(['accepted', 'rejected']),
    semanticVersionId: z.string().uuid(),
    reason: z.string().trim().min(10).max(500),
    decidedByMembershipId: z.string().uuid(),
    decidedAt: z.string().datetime({ offset: true }),
});
export const scanningMediaCoordinateCandidateSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaCoordinateContinuityCapability),
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    candidateNumber: z.number().int().positive(),
    visitFrameId: scanningMediaStableKeySchema,
    semanticVersionId: z.string().uuid(),
    sourceSetFingerprintSha256: scanningMediaSha256Schema,
    sources: z.array(scanningMediaCoordinateSourceEvidenceSchema).min(2).max(50),
    relationships: z.array(scanningMediaCoordinateRelationshipSchema).min(1).max(1_000),
    stitchedSnapshot: scanningMediaSemanticSnapshotSchema,
    validation: scanningMediaCoordinateValidationSchema,
    status: z.enum(['candidate', 'accepted', 'rejected']),
    decision: scanningMediaCoordinateDecisionSchema.nullable(),
    contentHash: scanningMediaSha256Schema,
    reason: z.string().trim().min(10).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const scanningMediaCoordinateStateSchema = z.strictObject({
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    latestCandidateId: z.string().uuid().nullable(),
    acceptedCandidateId: z.string().uuid().nullable(),
    acceptedSemanticVersionId: z.string().uuid().nullable(),
    acceptedAuthority: z.enum(['none', 'current', 'stale']),
    latestCandidate: scanningMediaCoordinateCandidateSchema.nullable(),
    acceptedCandidate: scanningMediaCoordinateCandidateSchema.nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
const scanningMediaCoordinateCommandBaseSchema = z.strictObject({
    commandId: z.string().uuid(),
    captureId: z.string().uuid(),
    expectedContinuityRevision: z.number().int().nonnegative(),
    reason: z.string().trim().min(10).max(500),
});
export const scanningMediaSaveCoordinateCandidateCommandSchema = scanningMediaCoordinateCommandBaseSchema.extend({
    kind: z.literal('save-candidate'),
    expectedCaptureRevision: z.number().int().positive(),
    visitFrameId: scanningMediaStableKeySchema,
    sources: z.array(scanningMediaCoordinateSourceDeclarationSchema).min(2).max(50),
    relationships: z.array(scanningMediaCoordinateRelationshipSchema).min(1).max(1_000),
});
export const scanningMediaAcceptCoordinateCandidateCommandSchema = scanningMediaCoordinateCommandBaseSchema.extend({
    kind: z.literal('accept-candidate'),
    expectedContinuityRevision: z.number().int().positive(),
    expectedCaptureRevision: z.number().int().positive(),
    candidateId: z.string().uuid(),
});
export const scanningMediaRejectCoordinateCandidateCommandSchema = scanningMediaCoordinateCommandBaseSchema.extend({
    kind: z.literal('reject-candidate'),
    expectedContinuityRevision: z.number().int().positive(),
    candidateId: z.string().uuid(),
});
export const scanningMediaCoordinateCommandSchema = z.discriminatedUnion('kind', [
    scanningMediaSaveCoordinateCandidateCommandSchema,
    scanningMediaAcceptCoordinateCandidateCommandSchema,
    scanningMediaRejectCoordinateCandidateCommandSchema,
]);
export const scanningMediaCoordinateStateResponseSchema = z.strictObject({
    data: scanningMediaCoordinateStateSchema.nullable(),
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const scanningMediaCoordinateCandidatesResponseSchema = z.strictObject({
    data: z.array(scanningMediaCoordinateCandidateSchema),
});
//# sourceMappingURL=scanning-media-coordinate-continuity.contract.js.map